import type { Collection } from '@prisma/client'
import { useFetcher } from '@remix-run/react'
import type { SerializeFrom } from '@remix-run/server-runtime'
import { PlusIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AvatarUpload } from '~/components/AvatarUpload'
import { ShopFooter } from '~/components/ShopLayout'
import { useOptionalSeller } from '~/hooks/data/use-seller'
import type { action } from '~/routes/shop.$shopSlug.collections.api/route.tsx'
import { Avatar, Button } from '~/ui'
import { cn } from '~/utils/classnames'
import { getImageAttributes } from '~/utils/image'

import {
  CollectionConfigurationActions,
  CollectionStatusBadge,
} from './collection-configuration-actions'
import {
  CollectionConfigurationContextProvider,
  useCollectionConfiguration,
} from './collection-configuration-context'
import { CollectionDetails } from './collection-details'
import { CollectionItemsDrawer } from './collection-items-drawer'
import { CollectionMediaGallery } from './collection-media-gallery'
import { CollectionProducts } from './collection-products'
import type {
  CollectionAttachments,
  SerializedCollectionItemWithProduct,
} from './type'
import { getCollectionFieldId } from './utils'
import { InnerPageTopNav } from '../inner-page-top-nav'

type CollectionDetailsProps = {
  collectionDetails?: {
    collection: SerializeFrom<Collection>
    collectionAttachments: CollectionAttachments
    collectionItemsSerialized: SerializedCollectionItemWithProduct[]
  }
}

const CollectionConfigurationForm = ({
  collectionDetails,
}: CollectionDetailsProps) => {
  const fetcher = useFetcher<typeof action>()

  return (
    <CollectionConfigurationContextProvider
      initialCollection={collectionDetails?.collection}
      initialCollectionItems={collectionDetails?.collectionItemsSerialized}
      initialCollectionAttachments={collectionDetails?.collectionAttachments}
      fetcher={fetcher}
    >
      <div
        className="flex min-h-full flex-col"
        inert={fetcher.state !== 'idle'}
      >
        <CollectionConfigurationContent />
      </div>
    </CollectionConfigurationContextProvider>
  )
}

const CollectionConfigurationContent = () => {
  const { t } = useTranslation()
  const [transientAvatarSrc, setTransientAvatarSrc] = useState<string | null>(
    null
  )
  const seller = useOptionalSeller()

  const { collection, collectionItems, dispatch, status, liveErrors } =
    useCollectionConfiguration()

  const [isCollectionItemsDrawerOpen, setIsCollectionItemsDrawerOpen] =
    useState(false)

  const avatarSrc = transientAvatarSrc ?? collection.avatarSrc

  const hasAvatarError = liveErrors.has(
    getCollectionFieldId(collection, 'avatarSrc')
  )

  const avatarRef = useRef<HTMLDivElement>(null)

  // Scroll to avatar when the error first appears
  useEffect(() => {
    if (hasAvatarError) {
      avatarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [hasAvatarError])

  return (
    <>
      <div className="bg-background relative flex grow flex-col justify-between rounded-t-2xl">
        <InnerPageTopNav>{seller && <CollectionStatusBadge />}</InnerPageTopNav>

        <Avatar
          ref={avatarRef}
          className={cn(
            'mx-auto mt-10 mb-5',
            hasAvatarError && 'animate-shake ring-destructive/40 ring-2'
          )}
          key={hasAvatarError ? 'error' : 'ok'}
        >
          <Avatar.Fallback />
          {avatarSrc && (
            <Avatar.Image
              {...getImageAttributes(avatarSrc, {
                width: 100,
                height: 100,
                format: 'webp',
              })}
            />
          )}
          {seller && (
            <AvatarUpload
              avatarSrc={avatarSrc}
              onUploadSuccess={(url) => {
                setTransientAvatarSrc(url)

                dispatch({
                  type: 'patch',
                  payload: {
                    avatarSrc: url,
                  },
                })
              }}
            />
          )}
        </Avatar>

        {/* collection name & description */}
        <CollectionDetails />

        {/* collection media gallery - empty state and filled state */}
        <CollectionMediaGallery />

        <section className="flex-1">
          {/* collection products - empty state and filled state */}
          <CollectionProducts
            onAddProduct={() => setIsCollectionItemsDrawerOpen(true)}
          />

          {/* add product button */}
          {seller && collectionItems?.length > 0 && (
            <div
              className={cn(
                'sticky bottom-20 z-20 mx-auto mt-8 mb-4 w-fit',
                status === 'draft' && 'bottom-30 lg:bottom-20'
              )}
            >
              <Button
                size="lg"
                variant="primary"
                className="cursor-pointer"
                onClick={() => {
                  setIsCollectionItemsDrawerOpen(true)
                }}
              >
                <PlusIcon className="opacity-64" />
                {t('action.add_product')}
              </Button>
            </div>
          )}
        </section>

        {seller && (
          <div
            className={cn(
              'w-full p-3',
              'border-border/4 bg-background sticky bottom-0 z-50 border-t'
            )}
          >
            <CollectionConfigurationActions />
          </div>
        )}

        <ShopFooter />
      </div>

      <CollectionItemsDrawer
        collection={collection}
        existingItems={collectionItems}
        isOpen={isCollectionItemsDrawerOpen}
        onOpenChange={setIsCollectionItemsDrawerOpen}
        onClose={() => setIsCollectionItemsDrawerOpen(false)}
      />
    </>
  )
}

export default CollectionConfigurationForm
